/**
 * MyRacketLabOpsPage — "Vista operativa" Racket Lab
 *
 * FINAL-DEMO-PROFILES-QUESTIONNAIRES-RL-OPS-FIX-1 (Feb 2026):
 * Frontend-only Preview-controlled dashboard for ops-track profiles
 * (`racket_technician`, `shop_manager`, `operator`, `stringer`).
 *
 * The page is NOT a `Próximamente` placeholder anymore. It surfaces a
 * controlled-mock summary (KPI cards + ticket queue table) so José /
 * Lumia can demo the operational surface visually without any backend
 * runtime. The "Crear ticket" CTA is intentionally disabled —
 * activation is gated by `PLAN RACKET-LAB-OPS-TICKETS-CREATE-1`.
 *
 * Defensive redirect: non-ops profiles → /app. Staff (admin/staff/qa_*)
 * see the same Preview AND a deep link to the real `/admin/racket-lab`.
 *
 * NO BACKEND. NO DB writes. NO new endpoints. Hard blocks intact.
 */
import React from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/i18n";
import PanelLayout from "@/components/layout/PanelLayout";
import {
  Wrench, ShieldCheck, ArrowUpRight, ClipboardList, Clock,
  CheckCircle2, Loader2, CalendarClock, PlusCircle, Lock, Store,
} from "lucide-react";

const OPS_PROFILE_KEYS = new Set([
  "racket_technician", "shop_manager", "operator", "stringer",
]);
const STAFF_ROLES_WITH_ADMIN_CONSOLE = new Set([
  "admin", "staff", "qa_manager", "qa_tester",
]);

const COPY = {
  es: {
    eyebrow: "Racket Lab",
    badge: "Preview controlado",
    title: "Vista operativa",
    intro:
      "Resumen de tu actividad operativa: tickets asignados, cola pendiente, próximas reservas y servicios disponibles. Datos de demostración bajo entorno controlado.",
    kpiAssigned: "Asignados hoy",
    kpiPending: "Pendientes",
    kpiInProgress: "En proceso",
    kpiCompleted: "Completados hoy",
    kpiNextSlot: "Próximo turno",
    shopLabel: "Tienda / Taller",
    shopValueDemo: "SS8 Demo Shop · ES",
    queueTitle: "Cola de trabajo asignado",
    queueSub: "Tickets demo asociados a tu cuenta Preview. No corresponden a clientes reales.",
    cols: {
      id: "Ticket",
      client: "Jugador / Cliente",
      racket: "Raqueta / Material",
      service: "Servicio",
      status: "Estado",
      priority: "Prioridad",
      due: "Fecha",
      action: "Acción",
    },
    statusOpen: "Pendiente",
    statusInProgress: "En proceso",
    statusDone: "Completado",
    priorityHigh: "Alta",
    priorityMedium: "Media",
    priorityLow: "Baja",
    actionView: "Ver",
    actionStart: "Iniciar",
    actionFinish: "Cerrar",
    actionDisabled: "Bajo GO nominal",
    createCta: "Crear ticket",
    createDisabledTip: "Acción bloqueada en Preview · pendiente PLAN RACKET-LAB-OPS-TICKETS-CREATE-1",
    adminConsoleTitle: "Consola operativa completa",
    adminConsoleBody: "Como cuenta de staff/admin, tienes acceso a la consola real de Racket Lab con tickets, calendario, inventario y RBAC completo.",
    openAdminConsole: "Abrir consola admin de Racket Lab",
    previewFooter: "Datos de demostración · sin escritura en base de datos · sin endpoints nuevos.",
  },
  en: {
    eyebrow: "Racket Lab",
    badge: "Controlled preview",
    title: "Operational view",
    intro:
      "Summary of your operational activity: assigned tickets, pending queue, upcoming bookings and available services. Demo data under controlled environment.",
    kpiAssigned: "Assigned today",
    kpiPending: "Pending",
    kpiInProgress: "In progress",
    kpiCompleted: "Completed today",
    kpiNextSlot: "Next slot",
    shopLabel: "Shop / Workshop",
    shopValueDemo: "SS8 Demo Shop · ES",
    queueTitle: "Assigned work queue",
    queueSub: "Demo tickets attached to your Preview account. They do NOT correspond to real customers.",
    cols: {
      id: "Ticket",
      client: "Player / Customer",
      racket: "Racket / Material",
      service: "Service",
      status: "Status",
      priority: "Priority",
      due: "Date",
      action: "Action",
    },
    statusOpen: "Pending",
    statusInProgress: "In progress",
    statusDone: "Completed",
    priorityHigh: "High",
    priorityMedium: "Medium",
    priorityLow: "Low",
    actionView: "View",
    actionStart: "Start",
    actionFinish: "Close",
    actionDisabled: "Under nominal GO",
    createCta: "Create ticket",
    createDisabledTip: "Action blocked in Preview · pending PLAN RACKET-LAB-OPS-TICKETS-CREATE-1",
    adminConsoleTitle: "Full operational console",
    adminConsoleBody: "As staff/admin you have access to the real Racket Lab console with tickets, calendar, inventory and full RBAC.",
    openAdminConsole: "Open Racket Lab admin console",
    previewFooter: "Demo data · no database writes · no new endpoints.",
  },
};

const STATUS_BADGE = {
  open: "bg-amber-100 text-amber-800",
  in_progress: "bg-sky-100 text-sky-800",
  done: "bg-emerald-100 text-emerald-800",
};

const PRIORITY_BADGE = {
  high: "bg-red-100 text-red-800",
  medium: "bg-amber-100 text-amber-800",
  low: "bg-slate-100 text-slate-700",
};

// Static demo dataset — purely frontend; reproducible across reloads.
const DEMO_TICKETS = [
  { id: "RL-DEMO-0142", client: "Alex P. · Player demo",     racket: "Wilson Pro Staff 97 v14",   service: "Encordado completo · Luxilon 1.25",  status: "in_progress", priority: "high",   due: "Hoy 12:30" },
  { id: "RL-DEMO-0143", client: "Emma J. · Parent (tutor)",  racket: "Babolat Pure Aero 2023 jr", service: "Cambio de overgrip · revisión",      status: "open",        priority: "medium", due: "Hoy 16:00" },
  { id: "RL-DEMO-0144", client: "Li M. · Player demo (ZH)",  racket: "Yonex EZONE 100",           service: "Encordado mixto · personalizado",     status: "open",        priority: "low",    due: "Mañana 10:00" },
  { id: "RL-DEMO-0145", client: "Coach C. · Demo academy",   racket: "Head Speed MP 2024 (×3)",   service: "Lote — encordado equipo",             status: "in_progress", priority: "medium", due: "Mañana 18:00" },
  { id: "RL-DEMO-0141", client: "Sofia B. · Club director",  racket: "Tecnifibre TFight 305",     service: "Revisión + plomado balance",          status: "done",        priority: "low",    due: "Ayer" },
];

function StatusBadge({ status, copy }) {
  const cls = STATUS_BADGE[status] || "bg-slate-100 text-slate-700";
  const label =
    status === "in_progress" ? copy.statusInProgress
    : status === "done"       ? copy.statusDone
    : copy.statusOpen;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${cls}`}>
      {label}
    </span>
  );
}

function PriorityBadge({ priority, copy }) {
  const cls = PRIORITY_BADGE[priority] || "bg-slate-100 text-slate-700";
  const label =
    priority === "high"   ? copy.priorityHigh
    : priority === "low"  ? copy.priorityLow
    : copy.priorityMedium;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${cls}`}>
      {label}
    </span>
  );
}

function KpiCard({ icon: Icon, label, value, testid, tone = "slate" }) {
  const toneCls =
    tone === "amber"   ? "bg-amber-50 border-amber-200 text-amber-900"
    : tone === "sky"   ? "bg-sky-50 border-sky-200 text-sky-900"
    : tone === "emerald"? "bg-emerald-50 border-emerald-200 text-emerald-900"
    : tone === "violet"? "bg-violet-50 border-violet-200 text-violet-900"
    : "bg-white border-slate-200 text-slate-900";
  return (
    <div className={`rounded-xl border p-4 ${toneCls}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider opacity-80">
        <Icon className="w-4 h-4" />
        {label}
      </div>
      <div className="mt-2 text-2xl font-bold">{value}</div>
    </div>
  );
}

export default function MyRacketLabOpsPage() {
  const { user } = useAuth();
  const { lang } = useLang();
  const copy = COPY[lang] || COPY.en;

  if (user) {
    const isOpsProfile = OPS_PROFILE_KEYS.has(user.profile_key);
    const hasAdminConsole = STAFF_ROLES_WITH_ADMIN_CONSOLE.has(user.role);
    if (!isOpsProfile && !hasAdminConsole) {
      return <Navigate to="/app" replace />;
    }
  }

  const showAdminConsoleHint =
    user && STAFF_ROLES_WITH_ADMIN_CONSOLE.has(user.role);

  const assigned = DEMO_TICKETS.length;
  const pending  = DEMO_TICKETS.filter((t) => t.status === "open").length;
  const inProg   = DEMO_TICKETS.filter((t) => t.status === "in_progress").length;
  const doneToday= DEMO_TICKETS.filter((t) => t.status === "done").length;
  const nextSlot = DEMO_TICKETS.find((t) => t.status !== "done")?.due || "—";

  return (
    <PanelLayout>
      <div data-testid="page-rl-ops">
        <header className="flex flex-wrap items-center gap-3 mb-2">
          <Wrench className="w-6 h-6 text-emerald-700" />
          <div className="tsw-overline">{copy.eyebrow}</div>
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-semibold uppercase tracking-wider"
            data-testid="page-rl-ops-badge"
          >
            <ShieldCheck className="w-3 h-3" />{copy.badge}
          </span>
        </header>

        <h1 className="text-2xl font-bold text-slate-900" data-testid="page-rl-ops-title">
          {copy.title}
        </h1>
        <p className="text-sm text-slate-600 mt-1 max-w-2xl">{copy.intro}</p>

        {/* Shop / workshop association — demo value, no DB read */}
        <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700"
             data-testid="page-rl-ops-shop">
          <Store className="w-3.5 h-3.5" />
          <span className="opacity-70">{copy.shopLabel}:</span>
          <span>{copy.shopValueDemo}</span>
        </div>

        {/* KPI cards */}
        <div className="mt-5 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="page-rl-ops-kpis">
          <KpiCard icon={ClipboardList} label={copy.kpiAssigned}   value={assigned}  tone="slate"   testid="rl-ops-kpi-assigned" />
          <KpiCard icon={Clock}         label={copy.kpiPending}    value={pending}   tone="amber"   testid="rl-ops-kpi-pending" />
          <KpiCard icon={Loader2}       label={copy.kpiInProgress} value={inProg}    tone="sky"     testid="rl-ops-kpi-in-progress" />
          <KpiCard icon={CheckCircle2}  label={copy.kpiCompleted}  value={doneToday} tone="emerald" testid="rl-ops-kpi-done" />
          <KpiCard icon={CalendarClock} label={copy.kpiNextSlot}   value={nextSlot}  tone="violet"  testid="rl-ops-kpi-next-slot" />
        </div>

        {/* Queue table + CTA */}
        <section className="mt-6" data-testid="page-rl-ops-queue">
          <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
            <div>
              <h2 className="font-display font-semibold text-lg text-slate-900">{copy.queueTitle}</h2>
              <p className="text-xs text-slate-500 mt-0.5 max-w-2xl">{copy.queueSub}</p>
            </div>
            <button
              type="button"
              disabled
              aria-disabled="true"
              title={copy.createDisabledTip}
              className="inline-flex items-center gap-2 rounded-full bg-slate-200 text-slate-500 px-4 py-2 text-sm font-semibold cursor-not-allowed select-none"
              data-testid="rl-ops-create-ticket"
            >
              <Lock className="w-4 h-4" />
              <PlusCircle className="w-4 h-4" />
              {copy.createCta}
            </button>
          </div>

          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold">{copy.cols.id}</th>
                  <th className="text-left px-3 py-2 font-semibold">{copy.cols.client}</th>
                  <th className="text-left px-3 py-2 font-semibold">{copy.cols.racket}</th>
                  <th className="text-left px-3 py-2 font-semibold">{copy.cols.service}</th>
                  <th className="text-left px-3 py-2 font-semibold">{copy.cols.status}</th>
                  <th className="text-left px-3 py-2 font-semibold">{copy.cols.priority}</th>
                  <th className="text-left px-3 py-2 font-semibold">{copy.cols.due}</th>
                  <th className="text-right px-3 py-2 font-semibold">{copy.cols.action}</th>
                </tr>
              </thead>
              <tbody>
                {DEMO_TICKETS.map((tkt) => (
                  <tr key={tkt.id} className="border-t border-slate-100" data-testid={`rl-ops-row-${tkt.id}`}>
                    <td className="px-3 py-2 font-mono text-xs text-slate-700">{tkt.id}</td>
                    <td className="px-3 py-2 text-slate-800">{tkt.client}</td>
                    <td className="px-3 py-2 text-slate-600">{tkt.racket}</td>
                    <td className="px-3 py-2 text-slate-600">{tkt.service}</td>
                    <td className="px-3 py-2"><StatusBadge status={tkt.status} copy={copy} /></td>
                    <td className="px-3 py-2"><PriorityBadge priority={tkt.priority} copy={copy} /></td>
                    <td className="px-3 py-2 text-slate-600">{tkt.due}</td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        disabled
                        aria-disabled="true"
                        title={copy.createDisabledTip}
                        className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-500 cursor-not-allowed select-none"
                        data-testid={`rl-ops-action-${tkt.id}`}
                      >
                        <Lock className="w-3 h-3" />
                        {tkt.status === "done" ? copy.actionView
                          : tkt.status === "in_progress" ? copy.actionFinish
                          : copy.actionStart}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="mt-3 text-[11px] text-slate-500">{copy.previewFooter}</p>
        </section>

        {showAdminConsoleHint && (
          <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 p-5"
               data-testid="page-rl-ops-admin-hint">
            <h3 className="font-semibold text-emerald-900">{copy.adminConsoleTitle}</h3>
            <p className="text-sm text-emerald-800 mt-1">{copy.adminConsoleBody}</p>
            <Link
              to="/admin/racket-lab"
              className="inline-flex items-center gap-1.5 mt-3 text-sm font-semibold text-emerald-700 hover:text-emerald-900"
              data-testid="page-rl-ops-admin-link"
            >
              {copy.openAdminConsole}
              <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        )}
      </div>
    </PanelLayout>
  );
}
